import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Typography } from '@mui/material';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import {
  AutocompleteOptions,
  Button,
  Loader,
  SelectFieldWrapper,
} from '@Components/index';
import Modal from '@Components/Modal';
import commonConstants from '@Constants/common.constants';
import { CommonConstants } from '@Constants/index';
import {
  ProviderDialInNumberModalInputs,
  ProviderDialInNumberModalProps,
} from '@Containers/Settings/AdminSettings/ProviderDialInNumberModal/ProviderDialInNumberModal.types';
import { useBuyDialInNumberMutation } from '@Services/settings.service';
import {
  useGetAreaCodeListQuery,
  useGetAvailablePhoneNumbersMutation,
} from '@Services/userCallerIds.service';
import { BaseAlertProps } from '@Typings/global.types';
import useAlert from '@Utils/hooks/useAlert.hook';

const ProviderDialInNumberModal: FunctionComponent<ProviderDialInNumberModalProps> =
  (props: ProviderDialInNumberModalProps): JSX.Element => {
    const { isOpen, setIsOpen, availableLimitToBuy, voiceProvider } = props;

    const { t } = useTranslation();
    const { triggerAlert } = useAlert();

    const [availableDialInNumbers, setAvailableDialInNumbers] = useState<
      string[]
    >([]);
    const [alertState, setAlertState] = useState<BaseAlertProps>({
      openAlert: false,
    });

    // get list of all area codes
    const { data: dataOfAreaCodeList = [] } = useGetAreaCodeListQuery();

    // load available phone numbers by area code
    const [availableList, availableListMethods] =
      useGetAvailablePhoneNumbersMutation();

    // buy phone number
    const [buyDialInNumber, buyDialInNumberMethods] =
      useBuyDialInNumberMutation();

    const areaCodes = dataOfAreaCodeList.map((code) => ({
      label: `+1 (${code.areaCode})`,
      value: code.areaCode,
    }));

    const required = t('common.formErrorMessages.required');
    const methods = useForm<ProviderDialInNumberModalInputs>({
      resolver: yupResolver(
        yup.object({
          areaCode: yup
            .object()
            .nullable()
            .required(required)
            .shape({ value: yup.string(), label: yup.string() }),
        })
      ),
      mode: 'onChange',
      shouldUnregister: true,
    });

    /**
     * Buy dial in phone number Form Methods: Updates the form state on submit
     */
    const onSubmitHandler: SubmitHandler<ProviderDialInNumberModalInputs> = (
      formData
    ) => {
      if (formData) {
        return buyDialInNumber({
          areaCode: formData.areaCode.value,
          phoneAmount: '1',
          voiceProvider,
          phoneNumbers: availableDialInNumbers,
        });
      }
    };

    /**
     * Error and Success alerts for buying dial in phone number
     */
    useEffect(() => {
      if (buyDialInNumberMethods.error) {
        triggerAlert(buyDialInNumberMethods.error, setAlertState);
      }
      if (buyDialInNumberMethods.isSuccess) {
        setIsOpen(!buyDialInNumberMethods.data?.boughtPhones);
        if (buyDialInNumberMethods.data?.boughtPhones > 0) {
          triggerAlert(
            CommonConstants.GLOBAL_MESSAGES.DIAL_IN_NUMBER_PURCHASED,
            setAlertState,
            'success'
          );
        }
      }
    }, [
      buyDialInNumberMethods.isSuccess,
      buyDialInNumberMethods.error,
      buyDialInNumberMethods.data,
      triggerAlert,
      buyDialInNumberMethods,
      setIsOpen,
    ]);

    const areaCodeChangeHandler = (
      _event: React.SyntheticEvent<Element, Event>,
      value: AutocompleteOptions
    ) => {
      const selectedAreaCode = value.value;
      if (selectedAreaCode) {
        return availableList({
          areaCode: selectedAreaCode,
          limit: availableLimitToBuy.toString(),
          voiceProvider,
        });
      }
    };

    /**
     * Error and Success alerts for checking available phone numbers
     */
    useEffect(() => {
      if (availableListMethods.isSuccess) {
        if (availableListMethods.data.length > 0) {
          setAvailableDialInNumbers(
            availableListMethods.data.map((numbers) => numbers.phoneNumber)
          );
        } else {
          setAvailableDialInNumbers([]);
          triggerAlert(
            commonConstants.GLOBAL_MESSAGES.UNAVAILABLE_ROTATION_NUMBER,
            setAlertState,
            'error'
          );
        }
      }
    }, [availableListMethods, triggerAlert]);

    const closeModal = () => {
      setIsOpen(false);
      setAvailableDialInNumbers([]);
    };

    return (
      <>
        <Modal
          open={isOpen}
          headerText={t('adminSettings.Add New Dial In Phone', {
            providerName:
              voiceProvider.charAt(0).toUpperCase() +
              voiceProvider.slice(1).toLowerCase(),
          })}
          onClose={() => closeModal()}
          alertState={alertState}
        >
          <Box position="relative">
            {(availableListMethods.isLoading ||
              buyDialInNumberMethods.isLoading) && (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '70%',
                  backgroundColor: 'rgba(255, 255, 255, 0.7)',
                  zIndex: 10,
                }}
              >
                <Loader />
              </Box>
            )}
            <FormProvider {...methods}>
              <Box
                component="form"
                onSubmit={methods.handleSubmit(onSubmitHandler)}
              >
                <>
                  <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    sx={{ padding: '2rem 2.4rem 2.4rem' }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      mt={3.8}
                    >
                      <Typography variant="h2" mr={2}>
                        {`${t('userCallerIds.Area Code')}:`}
                      </Typography>
                      <SelectFieldWrapper
                        name="areaCode"
                        options={areaCodes}
                        multiple={false}
                        textFieldProps={{
                          placeholder: t('userCallerIds.Select Area Code'),
                        }}
                        onChange={areaCodeChangeHandler}
                        disableClearable
                        sx={{
                          minWidth: '18.6rem',
                          maxWidth: '18.6rem',
                        }}
                      />
                    </Box>
                    <Box py={4} px={11.5}>
                      <Typography variant="main" textAlign="center">
                        {t('adminSettings.Dial In Numbers Modal Message')}
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    display="flex"
                    flexDirection="column"
                    sx={{
                      borderTop: '0.1rem solid #A3ABB2',
                      padding: '3rem 2.4rem 2.4rem',
                    }}
                  >
                    <Box display="flex" justifyContent="flex-end">
                      <Button
                        onClick={() => closeModal()}
                        color="secondary"
                        variant="outlined"
                        sx={{ ml: 'auto', mr: 1.6 }}
                      >
                        {t('common.messages.Cancel')}
                      </Button>
                      <Button
                        type="submit"
                        color="primary"
                        disabled={availableDialInNumbers?.length < 1}
                      >
                        {t('common.messages.Submit')}
                      </Button>
                    </Box>
                  </Box>
                </>
              </Box>
            </FormProvider>
          </Box>
        </Modal>
      </>
    );
  };

export default ProviderDialInNumberModal;
